import React from 'react';
import { Box, Typography } from '@mui/material';
import { ProductsProvider } from 'stores/useProducts';
import useLayout from 'stores/useLayout';
import { getValueOrDefault } from 'utils/maybeTools';
import Product from '../Product/Product';
import { Category as CategoryType, MenuItem } from '../../generated/graphql';
import { ProductListing } from '../ProductListing';
import ProductFilters from '../ProductFilter/ProductFilters';
import { ListCarousel } from '../ListCarousel/ListCarousel';
import useGetProducts from '../../hooks/useGetProducts';
import ProductsFilterContainer from '../ProductsFilterContainer';
import SubCategory from './components/SubCategory';
import RichText from '../common/RichText';

type CategoryProps = CategoryType;

export function Category(category: CategoryProps) {
    return (
        <ProductsProvider>
            <ProvidedCategory {...category} />
        </ProductsProvider>
    );
}

export function ProvidedCategory({
    slug_path: slug,
    title,
    description,
    tag: maybeTag,
}: CategoryProps) {
    const tag = getValueOrDefault(maybeTag, undefined);

    const { products, message, loadMore } = useGetProducts({
        slug,
        options: {
            tag,
            includeVariants: Boolean(tag),
        },
    });

    const menu = useLayout((state) => state.menu);

    if (!menu) {
        return null;
    }

    if (message) {
        return <>{message}</>;
    }

    const matchedMenuItem = findMenuItemForCategory(slug, menu);

    return (
        <>
            <Box sx={{ marginBottom: 3 }}>
                <Typography variant="h1" gutterBottom>{title}</Typography>
                <RichText>{description}</RichText>
            </Box>
            <ListCarousel
                items={
                    (matchedMenuItem?.items ?? []).map((menuItem, index) => (
                        <SubCategory
                            menuItem={menuItem}
                            key={index}
                            boxProps={{ marginX: 0.5 }}
                        />
                    ))
                }
                carouselWrapperStyle={{ marginBottom: 3 }}
                listItemStyle={{ width: '100px', flexShrink: 0 }}
            />
            <ProductsFilterContainer>
                <ProductFilters />
                <Box marginBottom={8} marginTop={4}>
                    <ProductListing loadMore={loadMore}>
                        {products.map((product, index: number) => (
                            <Product
                                key={index}
                                product={product}
                            />
                        ))}
                    </ProductListing>
                </Box>
            </ProductsFilterContainer>
        </>
    );
}

function findMenuItemForCategory(slug: string, menu: MenuItem[]): MenuItem | undefined {
    for (const item of menu) {
        if (item.slug.includes(slug)) {
            return item;
        }
        if (item.items) {
            // Recursive loop to check all children of all elements in the menu
            const foundSubItem = findMenuItemForCategory(slug, item.items);
            if (foundSubItem) {
                return foundSubItem;
            }
        }
    }

    return undefined;
}
